/** A point on the earth, in the `{ lat, lon }` shape addresses use. */
export interface LatLon {
  lat: number;
  lon: number;
}

/**
 * Approximate distance in miles between two points (Haversine).
 *
 * Lifted out of LocationPicker, which had it privately for its
 * profile-vs-device mismatch check, once the order flow needed the same maths
 * to test a delivery address against a listing's drop-off radius. Two copies of
 * a distance function is how two surfaces end up disagreeing about how far
 * apart the same two points are.
 */
export function distanceMiles(a: LatLon, b: LatLon): number {
  const R = 3958.8;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLon = ((b.lon - a.lon) * Math.PI) / 180;
  const sinLat = Math.sin(dLat / 2);
  const sinLon = Math.sin(dLon / 2);
  const h =
    sinLat * sinLat +
    Math.cos((a.lat * Math.PI) / 180) *
      Math.cos((b.lat * Math.PI) / 180) *
      sinLon *
      sinLon;
  return 2 * R * Math.asin(Math.sqrt(h));
}

/**
 * Parse a listing's `obfuscatedLocation` ("lat,lng") into a point.
 *
 * That value carries a deliberate 50-100m offset, which is noise at the mile
 * granularity a drop-off radius is expressed in — fine for telling someone
 * whether they're plausibly in range. The authoritative check runs server-side
 * against the listing's real coordinates.
 */
export function parseObfuscated(value: string | null | undefined): LatLon | null {
  if (!value) return null;
  const [lat, lon] = value.split(',').map((n) => Number(n.trim()));
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
  return { lat, lon };
}
